/**
 * @module module/query-sk
 * @description <h2><code>query-sk</code></h2>
 *
 * Starting from a serialized paramtools.ParamSet, this control allows the user
 * to build up a query, suitable for passing to query.New.
 *
 * @evt query-change - The 'query-sk' element will produce 'query-change' events when the query
 *      parameters chosen have changed. The event contains the current selections formatted as a URL query, found in e.detail.q.
 *
 * @evt query-change-delayed - The same exact event as query-change, but with a 500ms delay after
 *      the query stops changing.
 *
 * @attr {string} current_query - The current query formatted as a URL formatted query string.
 * @attr {boolean} hide_invert - If the option to invert a query should be made available to
 *       the user.
 * @attr {boolean} hide_regex - If the option to include regex in the query should be made
 *       available to the user.
 * @attr {boolean} values_only - If true then only display the values selection and hide the key selection.
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: all[name]
    });
}
_export(exports, {
    removePrefix: function() {
        return removePrefix;
    },
    QuerySk: function() {
        return QuerySk;
    }
});
const _lit = require("lit");
const _decorators = require("lit/decorators.js");
const _query = require("../query");
const _selectsk = require("../../../elements-sk/modules/select-sk/select-sk");
const _queryvaluessk = require("../query-values-sk/query-values-sk");
require("../query-values-sk");
require("../../../elements-sk/modules/select-sk");
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
// The delay in ms before sending a delayed query-change event.
const DELAY_MS = 500;
const removePrefix = (s)=>{
    if (s.length === 0) {
        return s;
    }
    if ('~!'.includes(s[0])) {
        return s.slice(1);
    }
    return s;
};
let QuerySk = class QuerySk extends _lit.LitElement {
    get paramset() {
        return this._paramset;
    }
    set paramset(val) {
        this._paramset = val;
        this._originalParamset = val;
        this._recalcKeys();
        if (this._fastInput && this._fastInput.value.trim() !== '') {
            this._fastFilter(this._fastInput.value);
        }
    }
    get current_query() {
        return (0, _query.fromParamSet)(this._query);
    }
    set current_query(val) {
        const oldVal = this.current_query;
        if (val === oldVal) return;
        this._query = (0, _query.toParamSet)(val);
        this._rationalizeQuery();
        this.requestUpdate('current_query', oldVal);
    }
    get key_order() {
        return this._key_order;
    }
    set key_order(val) {
        this._key_order = val;
        this._recalcKeys();
        this.requestUpdate();
    }
    createRenderRoot() {
        return this;
    }
    _recalcKeys() {
        const keys = Object.keys(this._paramset);
        keys.sort();
        const pre = this._key_order.filter((ordered)=>keys.indexOf(ordered) > -1);
        const post = keys.filter((key)=>pre.indexOf(key) === -1);
        this._keys = pre.concat(post);
        if (this._keys.indexOf(this._selectedKey) === -1) {
            this._selectedKey = '';
        }
    }
    _rationalizeQuery() {
        const originalKeys = Object.keys(this._originalParamset);
        Object.keys(this._query).forEach((key)=>{
            if (originalKeys.indexOf(key) === -1) {
                delete this._query[key];
            } else {
                this._query[key] = this._query[key].filter((val)=>this._originalParamset[key].includes(val) || val.startsWith('~') || val.startsWith('!'));
            }
        });
    }
    _valuesChanged(e) {
        e.stopPropagation();
        const key = this._selectedKey;
        if (!key) return;
        if (this._fastInput && this._fastInput.value.trim() !== '') {
            // Things get complicated if the user has entered a filter. The user may
            // have selections in this._query[key] which don't appear in e.detail
            // because they have been filtered out, so we should only add/remove
            // values from this._query[key] that appear in this._paramset[key], while
            // being careful because value(s) may be prefixed with either a '!' or a
            // '~' if they are invert or regex queries.
            // When we toggle from a regex to a non-regex we need to clear the values.
            if (!e.detail.regex && this._query[key] && this._query[key].length && this._query[key][0].startsWith('~')) {
                this._query[key] = [];
            }
            if (e.detail.regex) {
                this._query[key] = e.detail.values;
            }
            this._applyInvert(key, e.detail.invert);
            const valuesDisplayed = new Set(this._paramset[key]);
            const currentQueryForKey = new Set(this._query[key]);
            const unprefixedSelectionsFromEvent = new Set(e.detail.values.map(removePrefix));
            valuesDisplayed.forEach((value)=>{
                const prefix = e.detail.invert ? '!' : '';
                const prefixedValue = prefix + value;
                if (unprefixedSelectionsFromEvent.has(value)) {
                    currentQueryForKey.add(prefixedValue);
                } else {
                    currentQueryForKey.delete(prefixedValue);
                }
            });
            this._query[key] = [
                ...currentQueryForKey
            ];
        } else {
            if (e.detail.regex) {
                this._query[key] = e.detail.values;
            } else {
                const merged = new Set(this._query[key] || []);
                e.detail.values.forEach((v)=>merged.add(v));
                this._query[key] = [
                    ...merged
                ];
            }
        }
        this._queryChanged();
    }
    _applyInvert(key, invert) {
        const values = this._query[key];
        if (!values || !values.length) {
            return;
        }
        const valuesHaveInvert = values[0].startsWith('!');
        if (invert === valuesHaveInvert) {
            return;
        }
        if (invert && !valuesHaveInvert) {
            this._query[key] = values.map((v)=>`!${v}`);
        } else {
            this._query[key] = values.map(removePrefix);
        }
    }
    _queryChanged() {
        this._rationalizeQuery();
        const new_query = this.current_query;
        if (this._lastQuery !== new_query) {
            this._lastQuery = new_query;
            this.requestUpdate();
            this.dispatchEvent(new CustomEvent('query-change', {
                detail: {
                    q: new_query
                },
                bubbles: true
            }));
            window.clearTimeout(this._delayedTimeout);
            this._delayedTimeout = window.setTimeout(()=>{
                this.dispatchEvent(new CustomEvent('query-change-delayed', {
                    detail: {
                        q: new_query
                    },
                    bubbles: true
                }));
            }, DELAY_MS);
        }
    }
    _keyChange(e) {
        const index = e.detail.selection;
        if (index >= 0 && index < this._keys.length) {
            this._selectedKey = this._keys[index];
        } else {
            this._selectedKey = '';
        }
        if (this._values) {
            this._values.clearFilter();
        }
    }
    _clear() {
        this._query = {};
        this._recalcKeys();
        this._queryChanged();
    }
    _fastFilterInput(e) {
        const val = e.target.value;
        this._fastFilter(val);
    }
    _fastFilter(val) {
        let filterString = '';
        if (val !== undefined) {
            filterString = val.trim();
        } else if (this._fastInput) {
            filterString = this._fastInput.value.trim();
        }
        const filters = filterString.toLowerCase().split(/\s+/).filter((f)=>f !== '');
        if (filters.length > 0) {
            this._filtering = true;
        } else {
            this._filtering = false;
        }
        const matches = (s)=>{
            s = s.toLowerCase();
            if (filters.length === 0) return true;
            return filters.filter((f)=>s.indexOf(f) > -1).length > 0;
        };
        const filtered = {};
        Object.keys(this._originalParamset).forEach((paramkey)=>{
            if (!this._filtering) {
                filtered[paramkey] = this._originalParamset[paramkey];
                return;
            }
            if (matches(paramkey)) {
                filtered[paramkey] = this._originalParamset[paramkey];
            } else {
                const valueMatches = [];
                this._originalParamset[paramkey].forEach((paramvalue)=>{
                    if (matches(paramvalue)) {
                        valueMatches.push(paramvalue);
                    }
                });
                if (valueMatches.length > 0) {
                    filtered[paramkey] = valueMatches;
                }
            }
        });
        this._paramset = filtered;
        this._recalcKeys();
    }
    _clearFilterButton() {
        if (!this._filtering) return (0, _lit.html)``;
        return (0, _lit.html)`<button @click=${this._clearFilter} class="clear_filters" title="Clear filter">
      &cross;
    </button>`;
    }
    _clearFilter() {
        if (this._fastInput) this._fastInput.value = '';
        this._paramset = this._originalParamset;
        this._filtering = false;
        this._recalcKeys();
    }
    render() {
        const key = this._selectedKey;
        const selectedKeyIndex = this._keys.indexOf(key);
        const options = key ? this._paramset[key] || [] : [];
        const selected = key ? this._query[key] || [] : [];
        return (0, _lit.html)`
      <div class="filtering">
        <input
          @input=${this._fastFilterInput}
          placeholder="Search Parameters and Values"
          name="query-sk-filter"
          autocomplete="off" />
        ${this._clearFilterButton()}
      </div>
      <div class="bottom">
        <div class="selection" ?hidden=${this.values_only}>
          <select-sk .selection=${selectedKeyIndex} @selection-changed=${this._keyChange}>
            ${this._keys.map((k)=>(0, _lit.html)`<div>${k}</div>`)}
          </select-sk>
          <button @click=${this._clear} class="clear_selections">Clear Selections</button>
        </div>
        <query-values-sk
          class=${!key ? 'hidden' : ''}
          .options=${options}
          .selected=${selected}
          @query-values-changed=${this._valuesChanged}
          ?hide_invert=${this.hide_invert}
          ?hide_regex=${this.hide_regex}></query-values-sk>
      </div>
    `;
    }
    selectKey(key) {
        if (this._keys.includes(key)) {
            this._selectedKey = key;
        }
    }
    removeKeyValue(key, value) {
        // Remove from _originalParamset to persist the removal through filters.
        if (this._originalParamset[key]) {
            const idx = this._originalParamset[key].indexOf(value);
            if (idx !== -1) {
                this._originalParamset[key].splice(idx, 1);
                // Refilter if needed, or just sync _paramset if not filtering.
                if (this._filtering) {
                    this._fastFilter();
                } else {
                    this._paramset = this._originalParamset;
                    this._recalcKeys();
                }
                this._rationalizeQuery();
                this._queryChanged();
                this.requestUpdate();
            }
        }
    }
    constructor(...args){
        super(...args);
        this.hide_invert = false;
        this.hide_regex = false;
        this.values_only = false;
        this._paramset = {};
        this._originalParamset = {};
        this._keys = [];
        this._query = {};
        this._key_order = [];
        this._filtering = false;
        this._delayedTimeout = null;
        this._lastQuery = '';
        this._selectedKey = '';
    }
};
_ts_decorate([
    (0, _decorators.property)({
        attribute: false
    }),
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [])
], QuerySk.prototype, "paramset", null);
_ts_decorate([
    (0, _decorators.property)({
        attribute: 'current_query',
        reflect: true
    }),
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [])
], QuerySk.prototype, "current_query", null);
_ts_decorate([
    (0, _decorators.property)({
        attribute: false
    }),
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [])
], QuerySk.prototype, "key_order", null);
_ts_decorate([
    (0, _decorators.property)({
        type: Boolean,
        reflect: true
    }),
    _ts_metadata("design:type", Boolean)
], QuerySk.prototype, "hide_invert", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Boolean,
        reflect: true
    }),
    _ts_metadata("design:type", Boolean)
], QuerySk.prototype, "hide_regex", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Boolean,
        reflect: true
    }),
    _ts_metadata("design:type", Boolean)
], QuerySk.prototype, "values_only", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", typeof _query.ParamSet === "undefined" ? Object : _query.ParamSet)
], QuerySk.prototype, "_paramset", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", typeof _query.ParamSet === "undefined" ? Object : _query.ParamSet)
], QuerySk.prototype, "_originalParamset", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Array)
], QuerySk.prototype, "_keys", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Boolean)
], QuerySk.prototype, "_filtering", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", String)
], QuerySk.prototype, "_selectedKey", void 0);
_ts_decorate([
    (0, _decorators.query)('select-sk'),
    _ts_metadata("design:type", typeof _selectsk.SelectSk === "undefined" ? Object : _selectsk.SelectSk)
], QuerySk.prototype, "_keySelect", void 0);
_ts_decorate([
    (0, _decorators.query)('query-values-sk'),
    _ts_metadata("design:type", typeof _queryvaluessk.QueryValuesSk === "undefined" ? Object : _queryvaluessk.QueryValuesSk)
], QuerySk.prototype, "_values", void 0);
_ts_decorate([
    (0, _decorators.query)('input[name="query-sk-filter"]'),
    _ts_metadata("design:type", typeof HTMLInputElement === "undefined" ? Object : HTMLInputElement)
], QuerySk.prototype, "_fastInput", void 0);
QuerySk = _ts_decorate([
    (0, _decorators.customElement)('query-sk')
], QuerySk);
